import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AdminBlog, AdminBlogSummary, AdminFaq, AdminPageMeta, AdminService, AdminServiceSummary,
  AdminSession, AdminUser, DashboardStats, FaqGroupSummary, ServiceKind
} from './admin.models';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

const SESSION_KEY = 'pixshiv_admin_session';

export const ADMIN_API_URL = `${environment.apiUrl}/controllers/admin.php`;

/**
 * Talks to api/controllers/admin.php. The bearer token is attached by authInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // ------------------------------------------------------------ session

  get session(): AdminSession | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      const session = raw ? (JSON.parse(raw) as AdminSession) : null;
      return session && session.expiresAt * 1000 > Date.now() ? session : null;
    } catch {
      return null;
    }
  }

  get isLoggedIn(): boolean {
    return this.session !== null;
  }

  private saveSession(session: AdminSession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  logout(): void {
    if (this.isBrowser) {
      localStorage.removeItem(SESSION_KEY);
    }
    this.router.navigate(['/admin/login']);
  }

  // ------------------------------------------------------------ transport

  private call<T>(action: string, payload: object = {}): Observable<T> {
    return this.http.post<ApiResponse<T>>(ADMIN_API_URL, { action, ...payload }).pipe(
      map(res => res.data),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && action !== 'login') {
          this.logout();
        }
        const message = err.error?.error || (err.status === 0
          ? 'Cannot reach the API. Check your connection or the server CORS settings.'
          : `Request failed (${err.status})`);
        return throwError(() => new Error(message));
      })
    );
  }

  // ------------------------------------------------------------ auth

  login(email: string, password: string): Observable<AdminSession> {
    return this.call<AdminSession>('login', { email, password }).pipe(tap(s => this.saveSession(s)));
  }

  // ------------------------------------------------------------ admin users

  listUsers(): Observable<AdminUser[]> {
    return this.call('users.list');
  }

  createUser(name: string, email: string, password: string): Observable<{ id: number }> {
    return this.call('users.create', { name, email, password });
  }

  resetUserPassword(id: number, password: string): Observable<boolean> {
    return this.call('users.resetPassword', { id, password });
  }

  deleteUser(id: number): Observable<boolean> {
    return this.call('users.delete', { id });
  }

  /** Changing the password signs out other sessions; this one continues with the fresh token */
  changePassword(currentPassword: string, newPassword: string): Observable<AdminSession> {
    return this.call<AdminSession>('account.password', { currentPassword, newPassword }).pipe(tap(s => this.saveSession(s)));
  }

  /** How the API sees this browser's IP (to configure TRUSTED_IP_HEADER behind a CDN) */
  networkInfo(): Observable<{ remoteAddr: string; usedIp: string; trustedIpHeader: string; forwardedHeaders: Record<string, string> }> {
    return this.call('system.network');
  }

  dashboard(): Observable<DashboardStats> {
    return this.call('dashboard');
  }

  // ------------------------------------------------------------ page meta

  listPageMeta(): Observable<AdminPageMeta[]> {
    return this.call('pageMeta.list');
  }

  savePageMeta(meta: AdminPageMeta): Observable<boolean> {
    return this.call('pageMeta.save', meta);
  }

  // ------------------------------------------------------------ blogs

  listBlogs(): Observable<AdminBlogSummary[]> {
    return this.call('blogs.list');
  }

  getBlog(slug: string): Observable<AdminBlog> {
    return this.call('blogs.get', { slug });
  }

  saveBlog(blog: AdminBlog, originalSlug: string | null): Observable<AdminBlog> {
    return this.call('blogs.save', { ...blog, originalSlug });
  }

  deleteBlog(slug: string): Observable<boolean> {
    return this.call('blogs.delete', { slug });
  }

  /** One-time upgrade of posts stored in the old section format to a single HTML body */
  convertLegacyBlogs(): Observable<{ converted: number }> {
    return this.call('blogs.convertLegacy');
  }

  // ------------------------------------------------------------ services

  listServices(kind: ServiceKind): Observable<AdminServiceSummary[]> {
    return this.call('services.list', { kind });
  }

  getService(kind: ServiceKind, slug: string): Observable<AdminService> {
    return this.call('services.get', { kind, slug });
  }

  saveService(service: AdminService, originalSlug: string | null): Observable<AdminService> {
    return this.call('services.save', { ...service, originalSlug });
  }

  deleteService(kind: ServiceKind, slug: string): Observable<boolean> {
    return this.call('services.delete', { kind, slug });
  }

  // ------------------------------------------------------------ FAQs

  listFaqGroups(): Observable<FaqGroupSummary[]> {
    return this.call('faqs.groups');
  }

  getFaqs(groupKey: string): Observable<AdminFaq[]> {
    return this.call('faqs.get', { groupKey });
  }

  saveFaqs(groupKey: string, items: AdminFaq[]): Observable<AdminFaq[]> {
    return this.call('faqs.save', { groupKey, items });
  }

  // ------------------------------------------------------------ uploads

  uploadImage(file: File): Observable<string> {
    const form = new FormData();
    form.append('action', 'upload');
    form.append('file', file);
    return this.http.post<ApiResponse<{ url: string }>>(ADMIN_API_URL, form).pipe(
      map(res => res.data.url),
      catchError((err: HttpErrorResponse) => throwError(() => new Error(err.error?.error || 'Upload failed')))
    );
  }
}

/** Turn a title into a URL slug */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 190);
}
